import { Box, inputBaseClasses, MenuItem, Paper, styled, TextField, useTheme } from "@mui/material";
import { gridClasses, GridColDef, GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { GetFieldLookup, GetFieldLookupItem, ILookup, useFormMetadata, useFormSave, useLookups, useSnackbar } from "@ngt/forms";
import { DateTime } from "luxon";
import { FC, useContext, useEffect, useMemo, useRef } from "react";
import { Outlet, useParams } from "react-router-dom";
import { FormDefinitions, Lot, LotStatuses, Permissions } from "../api/dtos";
import { StyledButton } from "../components/StyledButton";
import CollapsibleTablePremium from "../components/tables/CollapsibleTablePremium";
import TitleBox from "../components/TitleBox";
import { createCustomToolbarProps } from "../components/utility/CustomToolbarProps";
import ProjectContext from "../contexts/data/ProjectContext";
import StageContext from "../contexts/data/StageContext";
import usePermissions from "../hooks/auth/usePermissions";
import useLotsByProjectIdStageId from "../hooks/data/useLotsByProjectIdStageId";

const StyledPaper = styled(Paper) (({theme}) => ({
    marginBottom: theme.spacing(2)
}));

interface GridLookupParams extends GridRenderEditCellParams {
    lookups?: ILookup[]
}

const DataGridSelect:FC<GridLookupParams> = (props) => {
    const theme = useTheme();
    
    const apiRef = useGridApiContext();

    const ref = useRef<HTMLInputElement>(null);

    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = +event.target.value; // The new value entered by the user
        apiRef.current.setEditCellValue({ id: props.id, field: props.field, value: newValue });
    };

    useEffect(() => {
        if(ref?.current && props.hasFocus) {
            ref.current.focus();
        }
    }, [props.hasFocus]);

    return (
        <TextField
                inputRef={ref}
                select
                value={props.value}
                fullWidth
                autoFocus={props.hasFocus}
                variant="standard"
                slotProps={{
                    input: {
                        disableUnderline: true
                    }
                }}
                sx={{
                    alignSelf: 'center',
                    flex: '1 1 auto',
                    justifyContent: 'stretch',
                }}
                onChange={handleValueChange}
            >
                {GetFieldLookup(props.lookups, "SubStage")?.items?.map(x => (
                    <MenuItem 
                        key={x.id?.toString() ?? ""} 
                        value={x.id?.toString() ?? ""}
                        sx={{
                            paddingLeft: theme.spacing(1)
                        }}
                    >
                        {x.value?.toString()}
                    </MenuItem>
                ))}
        </TextField>
    );
}

const DataGridDatePicker:FC<GridRenderEditCellParams> = (props) => {
    const theme = useTheme();
    
    const ref = useRef<HTMLInputElement>(null);

    const apiRef = useGridApiContext();

    const handleValueChange = (value: any) => {
        const newValue = value as DateTime; // The new value entered by the user
        apiRef.current.setEditCellValue({ id: props.id, field: props.field, value: newValue?.toISO({includeOffset: false})});
    };
    
    useEffect(() => {
        if(ref?.current && props.hasFocus) {
            ref.current.focus();
        }
    }, [props.hasFocus]);

    return (
        <DatePicker
            inputRef={ref}
            format="dd/MM/yyyy"
            autoFocus={props.hasFocus}
            slotProps={{
                textField: {
                    fullWidth: true,
                    variant: "standard",
                    sx: {
                        alignSelf: 'center',
                        flex: '1 1 auto',
                        justifyContent: 'stretch',
                        border: "none",
                        margin: theme.spacing(0, 1),
                        [`& .${inputBaseClasses.root}::before`]: {
                            display: 'none'
                        },
                        [`& .${inputBaseClasses.root}::after`]: {
                            display: 'none'
                        }
                    },
                    slotProps: {
                        input: {
                            disableUnderline: true
                        },
                    }
                }
            }}
            value={props?.value ? DateTime.fromISO(props.value) : null}
            onChange={(value, _context) => handleValueChange(value as any)}
        />
    );
}

const DataGridNumeric:FC<GridRenderEditCellParams> = (props) => {
    const theme = useTheme();

    const apiRef = useGridApiContext();

    const ref = useRef<HTMLInputElement>(null);

    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value !== undefined && event.target.value != null && event.target.value != '' ? +event.target.value : undefined;
        apiRef.current.setEditCellValue({ id: props.id, field: props.field, value: newValue });
    };
    
    useEffect(() => {
        if(ref?.current && props.hasFocus) {
            ref.current.focus();
            ref.current.select();
        }
    }, [props.hasFocus]);

    return (
        <TextField
            inputRef={ref}
            value={props.value}
            variant="standard"
            autoFocus={props.hasFocus}
            sx={{
                alignSelf: 'center',
                flex: '1 1 auto',
                margin: theme.spacing(0, 1)
            }}
            slotProps={{
                input: {
                    type: "number",
                    disableUnderline: true,
                }
            }}
            onChange={(value) => handleValueChange(value as React.ChangeEvent<HTMLInputElement>)}
        />
    );
}

const useLotColumns = () => {

    const metadata = useFormMetadata(FormDefinitions.Lot);
    const {data: permissions} = usePermissions();
    const {data: project} = useContext(ProjectContext);

    const {data: lookups, loading: _lookupsLoading} = useLookups(metadata);

    const lotColumns = useMemo(() => {
        const columns: GridColDef<Lot>[] =  [
            {
                field: "number",
                headerName: "Lot Number",
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return (
                        <DataGridNumeric
                            {...params?? {}}
                            value={params.value}
                        />
                    )
                },
                editable: true,
                flex: 1
            },
            {
                field: "advertisedPrice",
                headerName: "Price",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return (
                        <DataGridNumeric
                            {...params?? {}}
                            value={params.value}
                        />
                    )
                },
                editable: permissions?.includes(Permissions.AdminAccess),
                flex: 1
            },
            {
                field: "dateOnMarket",
                headerName: "Release Date",
                editable: true,
                valueFormatter: (_value, row) => {
                    return row.dateOnMarket ? DateTime.fromISO(row.dateOnMarket).toFormat("dd/MM/yyyy") : undefined;
                },
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridDatePicker
                        {...params?? {}}
                        value={params.value ? params.value : ""} 
                    />
                ),
                flex: 1
            },
            {
                field: "approvedRebate",
                headerName: "Rebate",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridNumeric

                        {...params ?? {}}
                        value={params.row['approvedRebate']}
                    />
                ),
                editable: true,
                flex: 1
            },
            {
                field: "subStage",
                headerName: "SubStage",
                renderCell: (params) => GetFieldLookupItem(lookups, 'SubStage', params.row.subStage)?.value?.toString(),
                editable: true,
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridSelect
                        {...params?? {}}
                        lookups={lookups ?? []}
                        value={params.value ? params.value : ""} 
                    />
                ),
                flex: 1
            }
        ];

        const marginSchemeColumn: GridColDef<Lot> = {
                field: "landValue",
                headerName: "Land Value For Margin Scheme",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridNumeric
                        {...params?? {}}
                        value={params.row['landValue']}
                    />
                ),
                editable: true,
                flex: 1
        }

        if(project?.marginScheme) {
            columns.splice(2, 0, marginSchemeColumn);
        }

        return columns;
    }, [lookups, permissions, project]);

    return lotColumns;
}

export const BulkEditLots: FC = () => {
    const params = useParams();

    const projectId = Number(params.projectId);
    const stageId = Number(params.stageId);

    const lotMetadata= useFormMetadata(FormDefinitions.Lot);

    const save = useFormSave(lotMetadata);

    const theme = useTheme();
    
    const {data: lots, loading: lotsLoading, refresh: lotsRefresh} = useLotsByProjectIdStageId(projectId, stageId);

    const snackbar = useSnackbar();

    const addNewLot = async () => {
        let lot = new Lot(
            {
                number: (lots?.reduce((a, b) => (
                    (a ?? 0) > (b.number ?? 0) ? a ?? 0 : b.number ?? 0
                ), 0) ?? 0) + 1, 
                status: LotStatuses.Available,
                stageId: stageId,
                advertisedPrice: 0,
                approvedRebate: 0
            }
        );
        await save(lot);
        await lotsRefresh();
        return lot;
    }

    const submit = async (newLot: Lot, oldLot: Lot) => {
        await save(newLot)
            .catch(reason => {
                    reason.responseStatus.errors.map((error: any) => 
                        snackbar.enqueueSnackbar(
                            error.message, 
                            { variant: "error-critical" }
                        )
                    )
                    newLot = oldLot;
                }
            );

        lotsRefresh();
        return newLot;
    }

    const {data: project} = useContext(ProjectContext);
    const {data: stage} = useContext(StageContext);

    
    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `${project?.name} - ${stage?.name} - Lots`,
        fileName: `${project?.name} - ${stage?.name} - Lots`,
        includeDateTime: true
    }), [project, stage]);    
    
    const columns = useLotColumns();

    return (

        //Properties

        //Summary

        //StageList
        <>
            <Outlet />

            <TitleBox>
                <StyledButton 
                    variant="contained"
                    onClick={addNewLot}
                >
                    Add Lot
                </StyledButton>
            </TitleBox>

            <StyledPaper>
                <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
                            
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                minHeight: 56,

                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                                    
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                                
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                },
                                
                                '&.column-group-header': {
                                    backgroundColor: theme.palette.grey[300],
                                    boxShadow: "1",
                                    textAlign: 'center'
                                }
                            }
                        },
                    }}
                    >
                    <CollapsibleTablePremium
                        density="compact"
                        editMode="row"
                        processRowUpdate={(newLot, oldLot, _params) => submit(newLot, oldLot)}
                        // onRowEditStart={(params, _event, details) => {
                        //     setTimeout(() => {
                        //         (details.api.getCellElement(params.row.id, params.field!)?.firstChild?.firstChild?.firstChild! as HTMLInputElement).focus();
                        //         (details.api.getCellElement(params.row.id, params.field!)?.firstChild?.firstChild?.firstChild! as HTMLInputElement).select();
                        //     }, 100);
                        // }}
                        //onRowEditStop={(params, event) => lotEditSubmit(params, event)}
                        startCollapsed={false} 
                        title={"Lots"} 
                        loading={lotsLoading} 
                        columns={columns}
                        rows={lots ?? []}
                        disableColumnMenu={false}
                        disableColumnFilter={false}
                        {...customToolbarProps}
                    />
                </Box>
            </StyledPaper>
        </>
    );
}